import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ClockIcon, 
  DevicePhoneMobileIcon, 
  StarIcon, 
  CheckCircleIcon,
  ChartBarIcon,
  AcademicCapIcon,
  BookOpenIcon,
  TrophyIcon,
  ArrowRightIcon,
  CalendarIcon,
  PlayIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  LightBulbIcon,
  UserIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { examData, getAvailableYears, getAvailableSessions, getDynamicExamData } from '../data/examData';
import { generateDefaultExamData } from '../utils/examDataUtils';
import { getQNetCertificationBySlug, convertQNetToExamFormat } from '../utils/qnetUtils';
import { useAuth } from '../contexts/AuthContext';
import Header from './common/Header';
import LoginPromptManager from './auth/LoginPromptManager';
import ExamScheduleCard from './ExamScheduleCard';
import PassRateChart from './PassRateChart';
import EligibilityChecker from './EligibilityChecker';

const ExamDetailPage = () => {
  const { examSlug } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loginWithSocial, loginAsGuest } = useAuth();
  const [examInfo, setExamInfo] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [relatedExams, setRelatedExams] = useState([]);
  
  // 연도/회차 선택 관련 상태
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [dynamicExamData, setDynamicExamData] = useState(null);
  const [showYearSelection, setShowYearSelection] = useState(false);
  
  // 검색 관련 상태
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(-1);
  const [searchHistory, setSearchHistory] = useState([]);
  
  // 로그인 프롬프트 상태
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  // 애니메이션 상태
  const [animatedStats, setAnimatedStats] = useState({
    totalExaminees: 0,
    averagePassRate: 0
  });
  
  // 마우스 추적 상태
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // 애니메이션 가시성 상태
  const [isVisible, setIsVisible] = useState({});

  // 마우스 추적 효과
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // 스크롤 애니메이션
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    // 관찰할 요소들
    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Dynamic exam data 로딩
  useEffect(() => {
    const loadDynamicData = async () => {
      try {
        const dynamicData = await getDynamicExamData();
        setDynamicExamData(dynamicData);
      } catch (error) {
        if (process.env.NODE_ENV === "development") console.warn('Failed to load dynamic exam data, using static fallback:', error);
        setDynamicExamData(examData);
      }
    };
    
    loadDynamicData();
  }, []);
  
  useEffect(() => {
    const loadExamInfo = async () => {
      const currentExamData = dynamicExamData || examData;
      
      // 1. 먼저 기존 examData에서 찾기 (CBT 지원 자격증)
      const examKey = Object.keys(currentExamData).find(key => 
        currentExamData[key].title === examSlug || 
        key === examSlug ||
        currentExamData[key].title.replace(/\s/g, '') === examSlug
      );

      if (examKey && currentExamData[examKey]) {
        // 기존 CBT 자격증 - 모든 기능 사용 가능
        setExamInfo({ ...currentExamData[examKey], key: examKey, isCBTSupported: true });
        
        const years = dynamicExamData ? 
          Object.keys(currentExamData[examKey]?.years || {}).map(year => parseInt(year)).sort((a, b) => b - a) :
          getAvailableYears(examKey);
        setAvailableYears(years);
        
        // 관련 자격증 추천 (같은 카테고리)
        const category = getExamCategory(examKey);
        const related = Object.keys(currentExamData)
          .filter(key => key !== examKey && getExamCategory(key) === category)
          .slice(0, 3)
          .map(key => ({ ...currentExamData[key], key }));
        setRelatedExams(related);
      } else {
        // 2. Q-Net 데이터에서 찾기 (618개 자격증)
        try {
          const qnetCert = await getQNetCertificationBySlug(examSlug);
          if (qnetCert) {
            // 배치 데이터에서 상세 정보 가져오기
            const examInfo = convertQNetToExamFormat(qnetCert, false);
            
            // 배치에서 생성된 상세 정보 추가 (daily-batch.js에서 생성)
            try {
              const detailedInfoResponse = await fetch('/qnet-detailed-info.json');
              if (detailedInfoResponse.ok) {
                const detailedInfo = await detailedInfoResponse.json();
                const certDetails = detailedInfo.detailedInfo?.[qnetCert.jmcd.toString().padStart(4, '0')];
                if (certDetails) {
                  examInfo.contents = certDetails.contents;
                  examInfo.eligibilityRequirements = certDetails.eligibilityRequirements;
                }
              }
            } catch (error) {
              if (process.env.NODE_ENV === "development") console.warn('배치 상세 정보 로드 실패:', error);
            }
            setExamInfo({ ...examInfo, key: examSlug });
            setAvailableYears([]);
            setRelatedExams([]);
          } else {
            // 3. 동적으로 기본 데이터 생성 (폴백)
            const defaultExamInfo = generateDefaultExamData(examSlug);
            setExamInfo({ ...defaultExamInfo, key: examSlug });
            const defaultYears = Object.keys(defaultExamInfo?.years || {}).map(year => parseInt(year)).sort((a, b) => b - a);
            setAvailableYears(defaultYears);
            setRelatedExams([]);
          }
        } catch (error) {
          if (process.env.NODE_ENV === "development") console.error('Q-Net 데이터 로딩 실패:', error);
          // 에러 시 기본 데이터 생성
          const defaultExamInfo = generateDefaultExamData(examSlug);
          setExamInfo({ ...defaultExamInfo, key: examSlug });
          setAvailableYears([]);
          setRelatedExams([]);
        }
      }
    };

    loadExamInfo();
  }, [examSlug, navigate, dynamicExamData]);
  
  // 검색 기록 로딩
  useEffect(() => {
    const savedHistory = localStorage.getItem('examSearchHistory');
    if (savedHistory) {
      setSearchHistory(JSON.parse(savedHistory));
    }
  }, []);
  
  // 키보드 이벤트 처리 (검색 결과 네비게이션)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isSearchFocused || searchResults.length === 0) return;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSearchIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSearchIndex(prev => 
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
      } else if (e.key === 'Enter' && selectedSearchIndex >= 0) {
        e.preventDefault();
        const selectedResult = searchResults[selectedSearchIndex];
        handleSearchSelect(selectedResult);
      } else if (e.key === 'Escape') {
        setIsSearchFocused(false);
        setSelectedSearchIndex(-1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSearchFocused, selectedSearchIndex, searchResults]);
  
  // 애니메이션 통계
  useEffect(() => {
    if (!examInfo || !examInfo.statistics) return;
    
    const targets = {
      totalExaminees: examInfo.statistics.totalExaminees,
      averagePassRate: examInfo.statistics.averagePassRate
    };
    
    const duration = 2000;
    const steps = 60;
    const stepTime = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      
      setAnimatedStats({
        totalExaminees: Math.floor(targets.totalExaminees * easeOutCubic),
        averagePassRate: (targets.averagePassRate * easeOutCubic).toFixed(1)
      });

      if (step >= steps) clearInterval(timer);
    }, stepTime);

    return () => clearInterval(timer);
  }, [examInfo]);

  const getExamCategory = (examKey) => {
    if (examKey.includes('engineer')) return 'engineer';
    if (examKey.includes('technician')) return 'technician';
    if (examKey.includes('industrial')) return 'industrial';
    return 'other';
  };

  // Q-Net API 데이터를 위한 examCode 매핑 (시험일정, 합격률, 자격요건 공통 사용)
  const getQNetExamCode = (examKey) => {
    // examData의 키를 Q-Net API 코드로 매핑
    const qnetCodeMapping = {
      'electrical-engineer': '1150', // 전기기사
      'electrical-industrial-engineer': '2140', // 전기산업기사
      'electrical-technician': '7780', // 전기기능사
      'electrical-construction-engineer': '1160', // 전기공사기사
      'information-processing-engineer': '1320', // 정보처리기사
      'computer-applications-level1': '2670', // 컴퓨터활용능력1급
      'computer-applications-level2': '2671', // 컴퓨터활용능력2급
      'architectural-engineer': '1630', // 건축기사
      'industrial-safety-engineer': '1431', // 산업안전기사
      'industrial-safety-industrial-engineer': '2381', // 산업안전산업기사
      'industrial-hygiene-engineer': '1441', // 산업위생관리기사
      'fire-engineer-mechanic': '1900', // 소방설비기사(기계분야)
      'fire-protection-engineer-electrical': '1910', // 소방설비기사(전기분야)
      'hvac-refrigeration-industrial-engineer': '2590', // 공조냉동기계산업기사
      'forklift-operator-technician': '7875', // 지게차운전기능사
      'excavator-operator': '7862', // 굴착기운전기능사
      'hazardous-materials-industrial-engineer': '2121', // 위험물산업기사
      'forest-technician': '7632', // 산림기능사
      'korean-cooking-technician': '7910', // 한식조리기능사
    };
    
    return qnetCodeMapping[examKey] || null;
  };

  // 시험일정 데이터를 위한 examCode 매핑 (기존 함수 유지)
  const getExamCodeForSchedule = (examKey) => {
    // examData의 키를 examSchedules.json의 키로 매핑
    const keyMapping = {
      // 대부분은 동일하지만 명시적 매핑으로 안정성 확보
      'electrical-engineer': 'electrical-engineer',
      'electrical-industrial-engineer': 'electrical-industrial-engineer', 
      'electrical-technician': 'electrical-technician',
      'information-processing-engineer': 'information-processing-engineer',
      'computer-applications-level1': 'computer-applications-level1',
      'computer-applications-level2': 'computer-applications-level2',
      'architectural-engineer': 'architectural-engineer',
      'forest-technician': 'forest-technician',
      'fire-engineer-mechanic': null, // 시험일정 데이터 없음
      'fire-engineer-electric': null, // 시험일정 데이터 없음
      'construction-machinery-mechanic': null, // 시험일정 데이터 없음
      'digital-convergence-technician': null, // 시험일정 데이터 없음
    };
    
    // 매핑된 키가 있으면 반환, 없으면 원래 키 시도
    const mappedKey = keyMapping[examKey];
    if (mappedKey !== undefined) {
      return mappedKey;
    }
    
    // 매핑에 없는 경우 원래 키 반환 (기존 동작 유지)
    if (process.env.NODE_ENV === "development") console.warn(`시험일정 키 매핑을 찾을 수 없습니다: ${examKey}`);
    return examKey;
  };

  const getCategoryName = (category) => {
    switch (category) {
      case 'engineer': return '기사';
      case 'technician': return '기능사';
      case 'industrial': return '산업기사';
      default: return '기타';
    }
  };

  const handleStartExam = (year, session) => {
    navigate(`/${examSlug}/${year}/${session}`);
  };

  const handleStartDiagnostic = () => {
    navigate(`/diagnostic/${examSlug}`);
  };
  
  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setSelectedSession(null);
    
    // 모바일에서 회차 선택 카드로 부드럽게 스크롤
    setTimeout(() => {
      const sessionCard = document.querySelector('#session-selection-card');
      if (sessionCard && window.innerWidth < 768) { // md 브레이크포인트 체크
        sessionCard.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);
  };

  const handleSessionSelect = (session) => {
    setSelectedSession(session);
    // Auto-start exam after session selection
    setTimeout(() => {
      navigate(`/${examSlug}/${selectedYear}/${session}`);
    }, 500);
  };
  
  // 검색 관련 함수들
  const handleSearch = (query) => {
    setSearchQuery(query);
    setSelectedSearchIndex(-1);
    
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    // 연도와 회차 검색
    const results = [];
    const currentExamData = dynamicExamData || examData;
    const currentExamInfo = currentExamData[examInfo?.key];
    
    if (currentExamInfo && currentExamInfo.years) {
      Object.keys(currentExamInfo.years).forEach(year => {
        const yearData = currentExamInfo.years[year];
        if (year.includes(query) || query.includes(year)) {
          results.push({
            type: 'year',
            value: parseInt(year),
            label: `${year}년`,
            description: `${Object.keys(yearData.sessions || {}).length}회차 available`
          });
        }
        
        Object.keys(yearData.sessions || {}).forEach(session => {
          if (session.includes(query) || query.includes(session)) {
            results.push({
              type: 'session',
              year: parseInt(year),
              value: parseInt(session),
              label: `${year}년 ${session}회`,
              description: '시험 시작'
            });
          }
        });
      });
    }
    
    setSearchResults(results.slice(0, 10)); // 최대 10개 결과
  };

  const handleSearchSelect = (result) => {
    // 검색 기록에 추가
    const newHistory = [result.label, ...searchHistory.filter(item => item !== result.label)].slice(0, 5);
    setSearchHistory(newHistory);
    localStorage.setItem('examSearchHistory', JSON.stringify(newHistory));
    
    // 검색 UI 닫기
    setIsSearchFocused(false);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedSearchIndex(-1);
    
    // 선택된 결과에 따라 동작
    if (result.type === 'year') {
      handleYearSelect(result.value);
      setShowYearSelection(true);
    } else if (result.type === 'session') {
      handleYearSelect(result.year);
      setShowYearSelection(true);
      setTimeout(() => {
        handleSessionSelect(result.value);
      }, 100);
    }
  };

  const clearSearchHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('examSearchHistory');
  };
  
  const handleLoginClick = async (provider) => {
    try {
      if (provider === 'guest') {
        loginAsGuest();
      } else {
        await loginWithSocial(provider);
      }
      setShowLoginPrompt(false);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error('Login failed:', error);
    }
  };
  
  const toggleYearSelection = () => {
    setShowYearSelection(!showYearSelection);
    if (!showYearSelection) {
      setTimeout(() => {
        const yearSection = document.querySelector('#year-selection-section');
        if (yearSection) {
          yearSection.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 100);
    }
  };

  if (!examInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">자격증 정보를 로딩 중...</p>
        </div>
      </div>
    );
  }

  const category = getExamCategory(examInfo.key);
  const categoryName = getCategoryName(category);
  
  // 연도/회차 선택 관련 데이터
  const currentYearData = selectedYear ? examInfo?.years?.[selectedYear] : null;
  const availableSessions = selectedYear && dynamicExamData ? 
    Object.keys(examInfo?.years?.[selectedYear]?.sessions || {}).map(session => parseInt(session)).sort() :
    (selectedYear ? getAvailableSessions(examInfo.key, selectedYear) : []);

  return (
    <>
      <Helmet>
        <title>{examInfo.title} CBT 기출문제 - 무료 모의고사 | CBT Study</title>
        <meta 
          name="description" 
          content={`${examInfo.title} 기출문제를 실제 CBT 환경에서 연습하세요. ${examInfo.subjects?.join(', ')} 과목 완벽 대비! 무료 모의고사로 합격률 UP!`} 
        />
        <meta 
          name="keywords" 
          content={`${examInfo.title}, ${examInfo.title} 기출문제, ${examInfo.title} CBT, ${examInfo.title} 모의고사, 자격증, 국가기술자격`} 
        />
        <meta property="og:title" content={`${examInfo.title} CBT 기출문제 - 무료 모의고사`} />
        <meta 
          property="og:description" 
          content={`${examInfo.title} 기출문제를 실제 CBT 환경에서 연습하세요. 무료 모의고사로 합격 준비!`} 
        />
        <meta property="og:url" content={`${window.location.origin}/${examSlug}`} />
        
        {/* 구조화 데이터 - 2025 업데이트 */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": `${examInfo.title} CBT 연습`,
            "description": `${examInfo.title} 자격증 기출문제 CBT 연습 시스템`,
            "provider": {
              "@type": "Organization",
              "name": "CBT Study",
              "url": window.location.origin
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "KRW",
              "availability": "https://schema.org/InStock",
              "category": "Educational"
            },
            "hasCourseInstance": {
              "@type": "CourseInstance",
              "courseMode": "online",
              "courseWorkload": "PT2H",
              "instructor": {
                "@type": "Person",
                "name": "CBT Study Team"
              },
              "startDate": "2024-01-01"
            },
            "educationalLevel": categoryName,
            "teaches": examInfo.subjects?.join(', '),
            "timeRequired": `PT${examInfo.timeLimit}M`,
            "numberOfQuestions": examInfo.totalQuestions,
            "url": `${window.location.origin}/${examSlug}`
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 relative overflow-hidden">
        {/* 마우스 추적 효과 */}
        <div 
          className="fixed w-32 h-32 rounded-full pointer-events-none z-10 opacity-10 bg-orange-500 blur-xl transition-all duration-300"
          style={{
            left: mousePosition.x - 64,
            top: mousePosition.y - 64,
          }}
        />

        {/* 헤더 */}
        <Header setShowLoginPrompt={setShowLoginPrompt} />


        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* 메인 헤더 */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 mb-4">
              <TrophyIcon className="w-4 h-4 mr-1" />
              {categoryName} 자격증
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mt-4 mb-4 px-4">
              <span className="bg-gradient-to-r from-gray-900 via-orange-600 to-gray-900 bg-clip-text text-transparent animate-pulse">
                {examInfo.title} CBT 기출문제
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-6 max-w-3xl mx-auto px-4">
              {examInfo.isCBTSupported ? (
                <>실제 CBT 시험환경에서 {examInfo.title} 기출문제를 연습하고, 온라인 모의고사로 완벽하게 합격을 준비하세요. {examInfo.description}</>
              ) : (
                <>{examInfo.description}</>
              )}
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
              <div className="flex items-center">
                <ClockIcon className="w-4 h-4 mr-1" />
                {examInfo.timeLimit}분
              </div>
              <div className="flex items-center">
                <BookOpenIcon className="w-4 h-4 mr-1" />
                {examInfo.totalQuestions}문항
              </div>
              <div className="flex items-center">
                <CheckCircleIcon className="w-4 h-4 mr-1" />
                합격점 {examInfo.passingScore}점
              </div>
            </div>
          </div>

          {/* 시작 버튼들 - CBT 지원 자격증만 */}
          {examInfo.isCBTSupported ? (
            <div className="text-center mb-8 sm:mb-12">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
                {/* 진단평가 진입 버튼 제거 (기능은 유지, 비표시) */}
                <button
                  onClick={() => navigate(`/diagnostic/${examSlug}?count=100`)}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transform hover:-translate-y-1"
                >
                  <SparklesIcon className="w-5 h-5 mr-2" />
                  실전 모의고사 (100문항)
                </button>
                <button
                  onClick={() => navigate(`/diagnostic/${examSlug}?pick=1&count=30`)}
                  className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transform hover:-translate-y-1"
                >
                  <LightBulbIcon className="w-5 h-5 mr-2" />
                  과목별 집중 연습
                </button>
                <button
                  onClick={toggleYearSelection}
                  className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transform hover:-translate-y-1"
                >
                  <BookOpenIcon className="w-5 h-5 mr-2" />
                  CBT 기출문제 연습하기
                </button>
              </div>
            </div>
          ) : (
            /* Q-Net 자격증용 대안 버튼 */
            <div className="text-center mb-8 sm:mb-12">
              <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6 mx-4">
                <h3 className="text-lg font-semibold text-orange-800 mb-2">CBT 서비스 준비중</h3>
                <p className="text-orange-600 mb-4">이 자격증의 CBT 문제는 현재 준비중입니다.</p>
                <a
                  href="https://www.q-net.or.kr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
                >
                  <BookOpenIcon className="w-5 h-5 mr-2" />
                  Q-Net 공식 홈페이지에서 자세히 보기
                </a>
              </div>
            </div>
          )}

          {/* 3가지 핵심 정보 카드 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8 sm:mb-12">
            {/* 시험일정 카드 */}
            <ExamScheduleCard 
              examCode={examInfo.isCBTSupported ? examInfo.key : examInfo.examCode} 
              className="lg:col-span-1" 
            />
            
            {/* 합격률 통계 카드 */}
            <PassRateChart 
              examCode={examInfo.isCBTSupported ? getQNetExamCode(examInfo.key) : examInfo.examCode} 
              className="lg:col-span-1" 
            />
            
            {/* 자격요건 확인 카드 */}
            <EligibilityChecker 
              examCode={examInfo.isCBTSupported ? getQNetExamCode(examInfo.key) : examInfo.examCode} 
              className="lg:col-span-1 xl:col-span-1" 
            />
          </div>

          {/* 상세 정보 섹션 - 2x2 그리드로 확장 */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 mb-8 sm:mb-12 shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {/* 첫 번째 행 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:col-span-2">
                {/* 시험 과목 */}
                <div>
                  <div className="flex items-center mb-6">
                    <BookOpenIcon className="w-6 h-6 text-orange-500 mr-3" />
                    <h3 className="text-xl font-bold text-gray-900">CBT 출제 과목</h3>
                  </div>
                  <div className="space-y-3">
                    {(examInfo?.subjects || []).map((subject, index) => (
                      <div key={index} className="flex items-center p-3 bg-orange-50 rounded-lg">
                        <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                          {index + 1}
                        </span>
                        <span className="text-gray-800 font-medium">{subject}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 자격 정보 - Q-Net API 데이터 */}
                <div>
                  <div className="flex items-center mb-6">
                    <TrophyIcon className="w-6 h-6 text-orange-500 mr-3" />
                    <h3 className="text-xl font-bold text-gray-900">자격 정보</h3>
                  </div>
                  <div className="space-y-4">
                    {examInfo.qnetDetails ? (
                      <>
                        <div>
                          <div className="font-semibold text-gray-700 mb-2">시행처</div>
                          <div className="text-gray-800 bg-orange-50 px-3 py-2 rounded-lg">
                            {examInfo.qnetDetails.examOrganization || '한국산업인력공단'}
                          </div>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-700 mb-2">관련학과</div>
                          <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                            {examInfo.qnetDetails.relatedMajors || '관련 전공학과 정보를 준비중입니다.'}
                          </div>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-700 mb-2">검정방법</div>
                          <div className="text-sm text-gray-700 flex items-center">
                            <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                            {examInfo.qnetDetails.testMethod || '필기 + 실기시험'}
                          </div>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-700 mb-2">합격기준</div>
                          <div className="text-orange-600 font-bold">
                            {examInfo.qnetDetails.passingCriteria || `${examInfo.passingScore}점 이상`}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <div className="font-semibold text-gray-700 mb-2">시행처</div>
                          <div className="text-gray-800 bg-orange-50 px-3 py-2 rounded-lg">
                            한국산업인력공단
                          </div>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-700 mb-2">검정방법</div>
                          <div className="text-sm text-gray-700 flex items-center">
                            <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                            필기 + 실기시험
                          </div>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-700 mb-2">합격기준</div>
                          <div className="text-orange-600 font-bold">
                            {examInfo.passingScore}점 이상
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 italic">
                          * 상세 정보는 연 1회 업데이트됩니다
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 두 번째 행 - 실제 데이터(학습가이드/취업정보)가 있을 때만 표시 (placeholder 노출 방지) */}
              {(examInfo.studyTips || examInfo.careerInfo) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:col-span-2">
                {/* 학습 가이드 */}
                <div>
                  <div className="flex items-center mb-6">
                    <LightBulbIcon className="w-6 h-6 text-orange-500 mr-3" />
                    <h3 className="text-xl font-bold text-gray-900">CBT 학습 가이드</h3>
                  </div>
                  <div className="space-y-4">
                    {examInfo.studyTips ? (
                      <>
                        <div>
                          <div className="font-semibold text-gray-700 mb-2">권장 학습기간</div>
                          <div className="text-orange-600 font-bold">{examInfo.studyTips.studyPeriod}</div>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-700 mb-2">핵심 포인트</div>
                          <ul className="space-y-1">
                            {examInfo.studyTips.keyPoints?.map((point, index) => (
                              <li key={index} className="text-sm text-gray-600 flex items-start">
                                <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </>
                    ) : (
                      <div className="text-gray-500 text-sm">학습 가이드 정보를 준비중입니다.</div>
                    )}
                  </div>
                </div>

                {/* 취업 정보 */}
                <div>
                  <div className="flex items-center mb-6">
                    <UserIcon className="w-6 h-6 text-orange-500 mr-3" />
                    <h3 className="text-xl font-bold text-gray-900">취업 정보</h3>
                  </div>
                  <div className="space-y-4 text-sm">
                    {examInfo.careerInfo ? (
                      <>
                        <div>
                          <div className="font-semibold text-gray-700 mb-1">주요 직무</div>
                          <div className="text-gray-600">{examInfo.careerInfo.jobProspects}</div>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-700 mb-1">예상 연봉</div>
                          <div className="text-orange-600 font-bold">{examInfo.careerInfo.salary}</div>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-700 mb-1">연계 자격증</div>
                          <div className="text-gray-600">{examInfo.careerInfo.advancement}</div>
                        </div>
                      </>
                    ) : (
                      <div className="text-gray-500 text-sm">취업 정보를 준비중입니다.</div>
                    )}
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>

          {/* 연도/회차 선택 섹션 */}
          {showYearSelection && (
            <div id="year-selection-section" className="mb-12">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {examInfo.title} CBT <span className="text-orange-600">회차선택</span>
                </h2>
                <p className="text-gray-600">원하는 연도와 회차를 선택하여 CBT를 시작하세요</p>
              </div>
              
              {/* 검색 기능 */}
              <div className="relative max-w-md mx-auto mb-8">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => {
                      setTimeout(() => setIsSearchFocused(false), 200);
                    }}
                    placeholder="예: 2023, 1회, 2023년 2회"
                    className="w-full px-4 py-3 pr-12 text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {searchQuery ? (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setSearchResults([]);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    ) : (
                      <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* 검색 결과 드롭다운 */}
                {isSearchFocused && (searchResults.length > 0 || searchHistory.length > 0) && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                    <div className="max-h-80 overflow-y-auto">
                      {searchResults.length > 0 && (
                        <div className="p-3">
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                            검색 결과
                          </div>
                          {searchResults.map((result, index) => (
                            <button
                              key={`${result.type}-${result.value || result.year}-${result.value}`}
                              onClick={() => handleSearchSelect(result)}
                              className={`w-full text-left p-3 rounded-lg hover:bg-orange-50 transition-colors ${
                                index === selectedSearchIndex ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                              }`}
                            >
                              <div className="font-medium text-gray-900">{result.label}</div>
                              <div className="text-sm text-gray-500">{result.description}</div>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {searchResults.length === 0 && searchHistory.length > 0 && (
                        <div className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              최근 검색
                            </div>
                            <button
                              onClick={clearSearchHistory}
                              className="text-xs text-orange-600 hover:text-orange-700"
                            >
                              지우기
                            </button>
                          </div>
                          {searchHistory.map((item, index) => (
                            <button
                              key={item}
                              onClick={() => handleSearch(item)}
                              className="w-full text-left p-2 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              <div className="text-gray-700">{item}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* 연도/회차 선택 UI */}
              <div className="flex justify-center px-4">
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 w-full max-w-4xl">
                  {/* 연도 선택 */}
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100 flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">연도 선택</h3>
                    <div className="space-y-3">
                      {availableYears.map((year) => (
                        <button
                          key={year}
                          onClick={() => handleYearSelect(year)}
                          className={`w-full p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-md hover:scale-105 transform ${
                            selectedYear === year
                              ? 'border-orange-500 bg-orange-50 shadow-lg scale-105'
                              : 'border-gray-200 bg-white hover:border-orange-300'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <CalendarIcon className="w-6 h-6 text-orange-500" />
                            <div className="text-left">
                              <div className="font-bold text-gray-900">{year}년</div>
                              <div className="text-sm text-gray-500">기출문제</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 회차 선택 */}
                  <div id="session-selection-card" className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100 flex-1 min-w-0">
                    {!selectedYear ? (
                      <div className="text-center py-8">
                        <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-gray-400 mb-2">연도를 선택하세요</h3>
                        <p className="text-sm text-gray-500">왼쪽에서 연도를 선택하면<br />회차 목록이 나타납니다</p>
                      </div>
                    ) : (
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4">
                          {selectedYear}년 회차 선택
                        </h3>
                        
                        <div className="space-y-3 mb-6">
                          {availableSessions.map((session) => (
                            <button
                              key={session}
                              onClick={() => handleSessionSelect(session)}
                              className="w-full p-4 bg-orange-50 hover:bg-orange-100 border-2 border-orange-200 hover:border-orange-400 rounded-xl transition-all duration-300 hover:shadow-md group hover:scale-105 transform animate-pulse"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                    <span className="text-lg font-bold text-white">{session}</span>
                                  </div>
                                  <div className="text-left">
                                    <div className="font-semibold text-gray-900">{session}회차</div>
                                    {currentYearData && (
                                      <div className="text-sm text-gray-600">
                                        합격률: <span className="font-semibold text-orange-600">{currentYearData.passRate}%</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center text-orange-600 group-hover:translate-x-1 transition-transform duration-300">
                                  <PlayIcon className="w-5 h-5" />
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>

                        {currentYearData && (
                          <div className="p-4 bg-orange-50 rounded-lg">
                            <h4 className="font-semibold text-orange-800 mb-2">{selectedYear}년 시험 정보</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span>난이도:</span>
                                <span className={`font-medium ${
                                  currentYearData.difficulty === 'hard' ? 'text-red-600' : 
                                  currentYearData.difficulty === 'medium' ? 'text-yellow-600' : 'text-green-600'
                                }`}>
                                  {currentYearData.difficulty === 'hard' ? '어려움' : 
                                   currentYearData.difficulty === 'medium' ? '보통' : '쉬움'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>합격률:</span>
                                <span className="font-medium text-orange-600">{currentYearData.passRate}%</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 최신 기출문제 미리보기 */}
          <div className="bg-white rounded-2xl p-8 mb-12 shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{examInfo.title} CBT 기출문제</h2>
              <button
                onClick={toggleYearSelection}
                className="text-orange-600 hover:text-orange-700 font-medium flex items-center"
              >
                <span>전체 보기</span>
                <ArrowRightIcon className="w-4 h-4 ml-1" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {availableYears.slice(0, 6).map(year => {
                const sessions = dynamicExamData ? 
                  Object.keys(examInfo?.years?.[year]?.sessions || {}).map(s => parseInt(s)).sort() :
                  getAvailableSessions(examInfo.key, year);
                return sessions.slice(0, 2).map(session => (
                  <button
                    key={`${year}-${session}`}
                    onClick={() => handleStartExam(year, session)}
                    className="p-4 border border-gray-200 rounded-xl hover:border-orange-300 hover:bg-orange-50 transition-all duration-300 text-left group hover:shadow-md hover:scale-102 transform"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-gray-900">{year}년 {session}회</div>
                        <div className="text-sm text-gray-600">{examInfo.totalQuestions}문항 · {examInfo.timeLimit}분</div>
                      </div>
                      <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors duration-200" />
                    </div>
                  </button>
                ));
              })}
            </div>
          </div>

          {/* 통계 정보 - 실제 데이터만 표시 (하드코딩 응시자수/합격률 제거, 합격률은 위 PassRateChart의 실제 Q-Net 데이터 사용) */}
          <div id="stats-section" className="grid grid-cols-1 max-w-sm mx-auto gap-4 sm:gap-6 mb-8 sm:mb-12 animate-on-scroll">
            <div className="bg-white rounded-2xl p-6 sm:p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100 group hover:scale-105">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <StarIcon className="w-6 h-6 sm:w-8 sm:h-8 text-orange-400" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {availableYears.length}개 연도
              </div>
              <div className="text-gray-600 font-medium text-sm sm:text-base">기출문제 보유</div>
            </div>
          </div>

          {/* 관련 자격증 추천 */}
          {relatedExams.length > 0 && (
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">관련 자격증</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {relatedExams.map(exam => (
                  <Link
                    key={exam.key}
                    to={`/${exam.title}`}
                    className="p-6 border border-gray-200 rounded-2xl hover:border-orange-300 hover:shadow-lg transition-all duration-300 group hover:scale-105 transform bg-white"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm text-orange-600 font-medium">{getCategoryName(getExamCategory(exam.key))}</div>
                      <ArrowRightIcon className="w-4 h-4 text-gray-400 group-hover:text-orange-500 transition-colors duration-200" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{exam.title}</h3>
                    <p className="text-sm text-gray-600">{exam.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 로그인 프롬프트 모달 */}
      {showLoginPrompt && (
        <LoginPromptManager
          userProgress={{
            solvedQuestions: 0,
            wrongAnswers: 0,
            sessionMinutes: 0
          }}
          isLoggedIn={isAuthenticated}
          onLogin={handleLoginClick}
          onClose={() => setShowLoginPrompt(false)}
          forceShow={showLoginPrompt}
        />
      )}
    </>
  );
};

export default ExamDetailPage;